import {right} from "@popperjs/core";
import {AxiosError} from "axios";
import {
    Key as KeyIcon,
    SecurityPass as TrustedIcon,
    SecurityPass as TrustUnknownIcon,
    WarningCircle as NotTrustedIcon
} from "iconoir-react";
import {useEffect, useState} from "react";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import {useTranslation} from "react-i18next";
import {ApiConfig} from "../api/admin/ApiConfig";
import {PGPApi, PGPKeyDetails, PGPPublicKeyValidatorFailureSeverity} from "../api/admin/generated";
import {Truncate} from "../common/Truncate";
import "./PGPKeyRow.scss"
import {useSpinnerStatusContext} from "../hook/UseSpinnerStatusContext";
import {DateUtils} from "../utils/DateUtils";
import {logger} from "../utils/Logger";

const pgpApi = new PGPApi(ApiConfig.config());

type OnClickKey = () => void;

interface PGPKeyColumnProps {
    value?: string
    children?: React.ReactNode
    className? : string
    maxLength? : number
    onClickKey?  : OnClickKey
}

const PGPKeyColumn = ({value, children, className, maxLength = 60, onClickKey}: PGPKeyColumnProps) => {
    return (
        <td onClick={onClickKey} className={"user-select-all " + className}>{children}<Truncate value={value} maxLength={maxLength}/></td>
    )
}

interface PGPKeyIDColumnProps {
    value?: string
    onClickKey?  : OnClickKey
}

const PGPKeyIDColumn = ({value, onClickKey}: PGPKeyIDColumnProps) => {
    return (
        <td onClick={onClickKey} className={"user-select-all"}>{value}</td>
    )
}

interface PGPEmailColumnProps {
    email: string[]
    className? : string
    maxLength? : number
    onClickKey?  : OnClickKey
}

const PGPEmailColumn = ({email, className, maxLength = 60, onClickKey} : PGPEmailColumnProps) => {
    return (
        <td onClick={onClickKey} className={"user-select-all " + className}>{email.map((e, index) => (
            <span key={e}>
                    <Truncate value={e} maxLength={maxLength}/>
                {index < email.length - 1 &&", "}
                </span>))}
        </td>
    )
}

function getUserIdCSSClassname(userID : string, revokedUserIds : string[], validUserIds : string[])
{
    if (revokedUserIds.includes(userID)) {
        return "pgp-userid-revoked";
    }
    else if (!validUserIds.includes(userID)) {
        return "pgp-userid-invalid";
    }

    return undefined;
}

interface PGPUserIDColumnProps {
    userIDs: string[]
    revokedUserIds : string[]
    validUserIds : string[]
    className? : string
    maxLength? : number
    onClickKey?  : OnClickKey
}

const PGPUserIDColumn = ({userIDs, revokedUserIds, validUserIds, className, maxLength = 60, onClickKey} : PGPUserIDColumnProps) => {
    return (
        <td onClick={onClickKey} className={"user-select-all " + className}>{userIDs.map((u, index) => (
            <span key={u} className={getUserIdCSSClassname(u, revokedUserIds, validUserIds)}>
                    <Truncate value={u} maxLength={maxLength}/>
                {index < userIDs.length - 1 &&", "}
                </span>))}
        </td>
    )
}

/**
 * Determines the CSS class name for a row based on the key validation result.
 */
const rowCssClass = (pgpKey: PGPKeyDetails) =>
{
    if (pgpKey.failureSeverity === PGPPublicKeyValidatorFailureSeverity.Critical) {
        return "tr-danger";
    }

    return undefined
}

interface PGPKeyRowProps {
    pgpKey: PGPKeyDetails
    children?: React.ReactNode
    onClickKey?  : OnClickKey
}

export const PGPKeyRow = ({pgpKey, children, onClickKey}: PGPKeyRowProps) =>
{
    /**
     * the revoked UserIDs
     */
    const [revokedUserIds, setRevokedUserIds] = useState<string[]>([]);
    /**
     * the revoked UserIDs
     */
    const [validUserIds, setValidUserIds] = useState<string[]>([]);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * i18next translation hook
     */
    const {t} = useTranslation();

    /**
     * Checks whether UserIDs are valid
     */
    useEffect(() =>
    {
        if (pgpKey.userIDs === undefined || pgpKey.userIDs === null) {
            return
        }

        for (const userID of pgpKey.userIDs)
        {
            if (userID === undefined || userID === null || userID.length === 0) {
                continue;
            }

            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count + 1);
            }

            pgpApi.pGPControllerIsUserIDValid(pgpKey.id, userID)
                .then(response =>
            {
                if (response.data) {
                    setValidUserIds(validUserIds =>[...validUserIds, userID]);
                }
            })
            .catch((err: Error | AxiosError) => {
                logger.error(`Error checking validation status for key ${pgpKey.keyID}`, err);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
        }
    }, [pgpKey.id,
        pgpKey.keyID,
        pgpKey.userIDs,
        setSpinnerStateCounter]);

    /**
     * Checks whether UserIDs are revoked
     */
    useEffect(() =>
    {
        if (pgpKey.userIDs === undefined || pgpKey.userIDs === null) {
            return
        }

        for (const userID of pgpKey.userIDs)
        {
            if (userID !== undefined && userID !== null && userID.length > 0)
            {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count + 1);
                }

                pgpApi.pGPControllerIsUserIDRevoked(pgpKey.id, userID)
                    .then(response =>
                    {
                        if (response.data) {
                            logger.debug(`UserId ${userID} is revoked`)
                            setRevokedUserIds(revokedUserIds =>[...revokedUserIds, userID]);
                        }
                    })
                    .catch((err: Error | AxiosError) => {
                        logger.error(`Error checking revocation status for key ${pgpKey.keyID}`, err);
                        // if an error occurs, assume it's revoked
                        setRevokedUserIds(revokedUserIds =>[...revokedUserIds, userID]);
                    })
                    .finally(() => {
                        if (setSpinnerStateCounter !== undefined) {
                            setSpinnerStateCounter(count => count - 1);
                        }
                    })
            }
        }
    }, [pgpKey.id,
        pgpKey.keyID,
        pgpKey.userIDs,
        setSpinnerStateCounter]);

    const trustIcon = () =>
    {
        if (pgpKey.valid === undefined) {
            return (
                <OverlayTrigger
                    placement={right}
                    overlay={
                        <Tooltip>
                            {t("pgp.trust-unknown")}
                        </Tooltip>
                    }
                >
                    <TrustUnknownIcon className="text-secondary table-icon-width table-icon-height ms-2" />
                </OverlayTrigger>
            );
        }

        if (pgpKey.valid) {
            return (
                <OverlayTrigger
                    placement={right}
                    overlay={
                        <Tooltip>
                            {t("pgp.trusted")}
                        </Tooltip>
                    }
                >
                    <TrustedIcon className="text-success table-icon-width table-icon-height ms-2"/>
                </OverlayTrigger>
            );
        }

        return (
            <OverlayTrigger
                placement={right}
                overlay={
                    <Tooltip>
                        {pgpKey?.failureMessage}
                    </Tooltip>
                }
            >
                <NotTrustedIcon className="text-danger table-icon-width table-icon-height ms-2" />
            </OverlayTrigger>
        )
    }

    return (
        <tr key={pgpKey.sha256Fingerprint} className={`text-nowrap ${rowCssClass(pgpKey)} ${onClickKey !== undefined ? "cursor-pointer" : undefined}`}>
            <PGPKeyColumn className="td-w-min">
                {children}

                {trustIcon()}

                {pgpKey.privateKeyAlias.length > 0 && (pgpKey.privateKeyAvailable ?
                        <OverlayTrigger
                            placement={right}
                            overlay={
                                <Tooltip>
                                    {t("certificates.private-key-available")}
                                </Tooltip>
                            }
                        >
                            <KeyIcon className="text-success table-icon-width table-icon-height ms-1"/>
                        </OverlayTrigger>
                        :
                        <OverlayTrigger
                            placement={right}
                            overlay={
                                <Tooltip>
                                    {t("certificates.private-key-not-accessible")}
                                </Tooltip>
                            }
                        >
                            <KeyIcon className="text-danger table-icon-width table-icon-height ms-1"/>
                        </OverlayTrigger>
                )}
            </PGPKeyColumn>
            <PGPKeyIDColumn value={pgpKey.keyID} onClickKey={onClickKey}/>
            <PGPEmailColumn email={[...pgpKey.email]} className="text-wrap" onClickKey={onClickKey}/>
            <PGPUserIDColumn userIDs={[...pgpKey.userIDs]} revokedUserIds={revokedUserIds} validUserIds={validUserIds} onClickKey={onClickKey} className="text-wrap"/>
            <PGPKeyColumn value={pgpKey.fingerprint} onClickKey={onClickKey}/>
            <PGPKeyColumn value={pgpKey.sha256Fingerprint} onClickKey={onClickKey}/>
            <PGPKeyColumn value={DateUtils.timestampToDateStringShort(pgpKey.creationDate)} onClickKey={onClickKey}/>
            <PGPKeyColumn value={DateUtils.timestampToDateStringShort(pgpKey.expirationDate)} onClickKey={onClickKey}/>
            <PGPKeyColumn value={pgpKey.privateKeyAlias} onClickKey={onClickKey}/>
            <PGPKeyColumn value={`${pgpKey.validForEncryption}`} onClickKey={onClickKey}/>
            <PGPKeyColumn value={`${pgpKey.validForSigning}`} onClickKey={onClickKey}/>
            <PGPKeyColumn value={DateUtils.timestampToDateStringShort(pgpKey.insertionDate)} onClickKey={onClickKey}/>
            <PGPKeyColumn value={`${pgpKey.keyLength}`} onClickKey={onClickKey}/>
            <PGPKeyColumn value={pgpKey.algorithm} onClickKey={onClickKey}/>
            <PGPKeyColumn value={`${pgpKey.masterKey}`} onClickKey={onClickKey}/>
            <PGPKeyColumn value={pgpKey.parentKeyID} onClickKey={onClickKey}/>
            <PGPKeyColumn value={`${pgpKey.valid}`} onClickKey={onClickKey}/>
            <PGPKeyColumn value={pgpKey.failureSeverity} onClickKey={onClickKey}/>
            <PGPKeyColumn value={pgpKey.failureMessage} className="text-danger" onClickKey={onClickKey}/>
            <PGPKeyColumn value={pgpKey.id} onClickKey={onClickKey}/>
        </tr>
    )
}
